import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import shoppingCartActions from "../../store/actions/shoppingCart/shoppingCart.actions";
import AlertService from "../../services/alerts/alert.service";
import ShoppingCartQuantityInputComponent from "../../components/shoppingCart/ShoppingCartQuantityInputComponent";
import TextAreaInputElementComponent from "../../components/general/elements/inputElements/TextAreaInputElementComponent";

import "../../styles/pages/shoppingCart/shoppingCartPageComponent.scss";
import shoppingCartService from "../../services/shoppingCart/shoppingCart.service";
import useAccount from "../../hooks/useAccount";
import orderActions from "../../store/actions/orders/order.actions";
import enctyptionHelper from "../../helpers/encryption.helper";
import textInputModel from "../../models/general/elements/inputElements/textInput.model";
import LoaderComponent from "../../components/general/LoaderComponent";
import productActions from "../../store/actions/items/item.actions";
import validationHelper from "../../helpers/validation.helper";
import numbersHelper from "../../helpers/numbers.helper";
import agentOrderActions from "../../store/actions/agentOrder/agentOrder.actions";

export const ShoppingCartPageComponent = ({ shoppingCartActions, orderActions }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItemList = useSelector((state) => state.shoppingCart.shoppingCartItems);
  // M3: when an agent/admin has picked a customer, the order is placed on that customer's behalf.
  const selectedCustomer = useSelector((state) => state.agentOrderReducer.selectedCustomer);
  const { isLoggedIn, userInfo } = useAccount();
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryItemsDialogOpened, setIsHistoryItemsDialogOpened] = useState(false);
  // 0030 (BUG-4): submit guard. submittingRef is a SYNCHRONOUS re-entrancy flag (updates immediately,
  // unlike the async isLoading state) so a fast double-click can't fire two POST /Order/Create calls.
  // idempotencyKeyRef holds one key per checkout attempt — reused on a retry of the same cart, reset
  // only after a confirmed success — so the backend short-circuits a duplicate to the existing order.
  const submittingRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (!isHistoryItemsDialogOpened && cartItemList.length > 0) {
      navigate(`?d=shoppingCart&action=recommendedItems`);
      setIsHistoryItemsDialogOpened(true);
    }
  }, [cartItemList, userInfo, productActions]);

  const addItemCartQuantity = (cartItem) => {
    const updatedCartItem = { ...cartItem, quantity: cartItem.quantity + cartItem.itemPackSize }; // Create a new object
    shoppingCartActions.updateCartItem(updatedCartItem);
  };

  const reduceItemCartQuantity = async (cartItem) => {
    const newQuantity = cartItem.quantity - cartItem.itemPackSize;

    if (newQuantity >= cartItem.itemPackSize) {
      const updatedCartItem = { ...cartItem, quantity: newQuantity }; // Create a new object
      shoppingCartActions.updateCartItem(updatedCartItem);
      return;
    }

    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך למחוק פריט זה מסל הפריטים שלך?", "שים לב! תוכל להוסיף את הפריט לסל שוב מעמוד הפריט", "כן", "לא");
    if (userChoice === true) shoppingCartActions.removeFromCart(cartItem.id);
  };

  const handleCommentChange = (event) => {
    const { value } = event.target;
    setComment(value);
  };

  const clearShoppingCartClicked = async (event) => {
    event.preventDefault();
    const userChoice = await AlertService.confirm("האם אתה בטוח שברצונך לרוקן את סל הקניות?", "שים לב, פעולה זו בלתי הפיכה", "כן", "לא");
    if (userChoice) {
      shoppingCartActions.clearCart();
    }
  };

  const handleCreateOrder = async () => {
    // 0030 (BUG-4): synchronous re-entrancy guard. A useRef updates immediately (the isLoading state
    // does not, so a rapid double-click would otherwise read a stale false on the 2nd click and fire a
    // 2nd POST). The 2nd click here sees true and returns. Released in finally so a validation early
    // return still lets the user retry.
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      if (!enableOrderButton()) {
        AlertService.warning("לא ניתן לבצע הזמנה", "על מנת לבצע הזמנה, יש להתחבר למשתמש אותו קיבלת. לקבלת משתמש ופרטים נוספים, יש ליצור איתנו קשר.");
        return;
      }

      // M3: agent/admin orders go to the selected customer; a business user orders for itself.
      // The server re-derives the placer from the token and re-checks the customer is allowed.
      // 0017 (D4): never fall back to an undefined id. An admin/agent has no businessCustomer, so if
      // their selection is missing we must BLOCK the order with a clear message rather than place it
      // against `undefined` (which would throw or, worse, silently target the wrong customer).
      const targetCustomerId = selectedCustomer?.customerId ?? userInfo?.businessCustomer?.customerId;
      if (!targetCustomerId) {
        AlertService.warning("לא ניתן לבצע הזמנה", "לא נבחר לקוח להזמנה. יש לבחור לקוח לפני ביצוע ההזמנה.");
        return;
      }

      setIsLoading(true);

      // 0030 (BUG-4): one idempotency key per checkout attempt. Generated lazily on the first submit
      // and kept on the ref, so a retry of the SAME cart (after a transient error) reuses it and the
      // backend returns the already-created order instead of a duplicate. Reset only after a confirmed
      // success below.
      if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();

      // Construct CreateOrderRequest
      const createOrderRequest = {
        userId: userInfo.id,
        idempotencyKey: idempotencyKeyRef.current,
        comment,
        customerId: targetCustomerId,
        totalPriceBeforeVat: shoppingCartService.calculateTotalPrice(cartItemList),
        totalPrice: shoppingCartService.calculateTotalPriceWithVat(cartItemList, 18),
        vatSum: shoppingCartService.calculateVatPrice(shoppingCartService.calculateTotalPrice(cartItemList), 18), // Calculate VAT sum if needed
        vatPercent: 18, // Add VAT percent if applicable
        cartItems: cartItemList.map((item) => ({
          id: item.id,
          rivhit_ItemId: item.itemId,
          itemDescription: item.itemDescription,
          salePriceInNIS: item.salePriceInNIS,
          quantity: item.quantity,
        })),
      };
      // Dispatch action to create order
      const response = await orderActions.addOrder(createOrderRequest);
      setIsLoading(false);

      // 0029 (BUG-1): treat any 200 with an orderNumber as SUCCESS. The order is already persisted
      // server-side; the only thing that can be missing is the Rivhit document link. Never let a
      // missing link (or any post-success step) throw the success into the error path.
      const orderNumber = response?.payload?.orderNumber;
      if (!orderNumber) {
        AlertService.error("שגיאה ביצירת ההזמנה", "נסה שוב מאוחר יותר.");
        return;
      }

      // 0030 (BUG-4): confirmed success — drop the key so the NEXT checkout starts a fresh idempotency
      // scope (a brand-new order + a new Rivhit document), rather than short-circuiting to this one.
      idempotencyKeyRef.current = null;

      // Encrypt the documentLink only when present; otherwise omit the param entirely (the
      // thank-you page renders the success state from orderNumber alone).
      let documentLinkParam = "";
      if (response.payload.documentLink) {
        const encryptedDocumentLink = enctyptionHelper.encrypt(response.payload.documentLink);
        documentLinkParam = `&documentLink=${encodeURIComponent(encryptedDocumentLink)}`;
      }
      // Navigate with URL parameters
      navigate(`/thank-you?orderNumber=${orderNumber}${documentLinkParam}`);

      shoppingCartActions.clearCart();
      // M3: reset the on-behalf selection so the next order starts clean.
      if (selectedCustomer) dispatch(agentOrderActions.clearSelectedCustomer());
    } catch (error) {
      console.error("Error creating order:", error);
      AlertService.error("שגיאה ביצירת ההזמנה", error.message || "נסה שוב מאוחר יותר.");
    } finally {
      // 0030 (BUG-4): always release the re-entrancy guard — on success (harmless, the page unmounts),
      // on a validation early-return, and on error (so the user can retry; the same key is reused).
      submittingRef.current = false;
    }
  };

  // Get Ellements Functions
  const getCartItemsListTitlesElement = () => {
    return (
      <div className="cartItemsListTitlesContainer">
        <div className="productTitle">מוצר</div>
        <div className="quantityTitle">כמות</div>
        <div className="totalAmountTitle">סה"כ</div>
      </div>
    );
  };

  const getCartItemsListElement = () => {
    return (
      <div className="cartItemsListContainer">
        {cartItemList.map((cartItem, idx) => (
          <div key={idx} className="cartItemCardContainer">
            {getCartItemContentElement(cartItem)}
            {getShoppingCartQuantityElement(cartItem)}
            {getPriceSummaryElement(cartItem)}
          </div>
        ))}
      </div>
    );
  };

  const getCartItemContentElement = (cartItem) => {
    return (
      <div className="itemContentContainer">
        <div className="itemImageContainer">
          <img src={cartItem.pictureUrl} alt={cartItem.itemDescription} />
        </div>
        <div className="itemDetailsContainer">
          <span>{cartItem.itemDescription}</span>
          <span>{cartItem.salePriceInNIS} ₪</span>
        </div>
      </div>
    );
  };

  const getShoppingCartQuantityElement = (cartItem) => {
    const shoppingCartQuantityProps = {
      cartItem,
      handleAdd: addItemCartQuantity,
      handleReduce: reduceItemCartQuantity,
    };

    return (
      <div className="itemCartQuantityContainer">
        <ShoppingCartQuantityInputComponent props={shoppingCartQuantityProps} />
      </div>
    );
  };

  const getPriceSummaryElement = (cartItem) => {
    const totalPrice = numbersHelper.formatToTwoDecimalPlaces(cartItem.salePriceInNIS * cartItem.quantity);
    return <span className="alignTextEnd cartItemCardPriceSummary">{totalPrice} ₪</span>;
  };

  const getOrderCommentsElement = () => {
    const inputProps = {
      ...textInputModel,
      label: "הערות להזמנה",
      name: "comment",
      value: comment,
      clearDesgin: true,
      customStyleClass: "customTextArea",
      onChange: handleCommentChange,
    };
    return <TextAreaInputElementComponent props={inputProps} />;
  };

  const getCartSummaryElement = (cartItem) => {
    return (
      <div className="summaryAndCheckOutContainer">
        <div className="summaryDetailsComtainer">
          <span>סה"כ ללא מע"מ</span>
          <span>{shoppingCartService.calculateTotalPrice(cartItemList)} ₪</span>
        </div>
        <div className="summaryDetailsComtainer">
          <span>מע"מ 18%</span>
          <span>{shoppingCartService.calculateVatPrice(shoppingCartService.calculateTotalPrice(cartItemList), 18)} ₪</span>
        </div>
        <div className="summaryDetailsComtainer">
          <span>סה"כ לתשלום</span>
          <span>{shoppingCartService.calculateTotalPriceWithVat(cartItemList, 18)} ₪</span>
        </div>

        <button className={`primaryButton ${enableOrderButton() ? "" : "disableButton"}`} onClick={handleCreateOrder} disabled={isLoading}>
          בצע הזמנה
        </button>
      </div>
    );
  };

  const enableOrderButton = () => {
    // A business user orders for itself; an agent/admin orders for a selected customer.
    if (isLoggedIn && (userInfo?.businessCustomer || selectedCustomer)) return true;
    return false;
  };

  const getNoItemsInCartElement = () => (
    <div className="noItemsInCartContainer">
      <h2>סל הקניות שלך ריק</h2>
      <button onClick={() => navigate("/")}>חזור לעמוד הראשי</button>
    </div>
  );

  // *******************************
  if (isLoading) return <LoaderComponent />;

  if (cartItemList.length === 0) return getNoItemsInCartElement();

  return (
    <div className="shoppingCartPageContainer container">
      <div className="cartHeaderContainer">
        <h1>סל הקניות שלי</h1>
        <button className="primaryButton" onClick={clearShoppingCartClicked}>
          איפוס סל קניות
        </button>
      </div>

      {selectedCustomer && (
        <div className="onBehalfBanner">
          הזמנה עבור לקוח: <b>{selectedCustomer.firstName} {selectedCustomer.lastName}</b> (קוד {selectedCustomer.customerId})
        </div>
      )}

      {getCartItemsListTitlesElement()}
      {getCartItemsListElement()}

      <div className="summaryContainer">
        {getOrderCommentsElement()}
        {getCartSummaryElement()}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => {
  return {
    shoppingCartActions: bindActionCreators(shoppingCartActions, dispatch),
    orderActions: bindActionCreators(orderActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShoppingCartPageComponent);
